import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { jotaiStore } from 'init/jotai';
import { useSetAtom } from 'jotai';
import { i18nKeys } from 'locales';
import { currentLanguageAtom, translationStateAtom } from 'locales/logic';
import { isDefined } from 'remeda';
import { ApplicationLocaleSchema } from 'types';
import { z } from 'zod/v4';

import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from './utils';

const { t } = jotaiStore.get(translationStateAtom);

const MAX_FILE_SIZE = 5000000;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

const avatarSchema = z
  .any()
  .refine(file => file.size <= MAX_FILE_SIZE, 'Max image size is 5MB.')
  .refine(file => ACCEPTED_IMAGE_TYPES.includes(file.type), 'Invalid image type.')
  .nullable();

export const UpdateProfileFormSchema = z
  .object({
    avatarUrl: z.url().nullable(),
    email: z.email({ error: t(i18nKeys.ERROR.INVALID_EMAIL) }),
    firstName: z.string().min(1, { error: t(i18nKeys.ERROR.FIELD_REQUIRED) }),
    lastName: z.string().min(1, { error: t(i18nKeys.ERROR.FIELD_REQUIRED) }),
    locale: ApplicationLocaleSchema,
    pageLimit: z.coerce.number<number | string>(),
    receiveDailyEmails: z.boolean(),
    receiveNotificationEmails: z.boolean(),
    receiveTaskEmails: z.boolean(),
    receiveWeeklyEmails: z.boolean(),
    signature: z.string().nullable(),
    avatar: avatarSchema,
    phone: z.string().nullable(),
  })
  .partial();

export type UpdateProfileForm = z.infer<typeof UpdateProfileFormSchema>;

function createFormData(data: UpdateProfileForm, schema: z.ZodType<UpdateProfileForm>) {
  const formData = new FormData();
  const parsedData = schema.parse(data);
  const hasAvatar = 'avatar' in parsedData;

  if (hasAvatar) {
    const { avatar } = parsedData;
    formData.append('avatar', avatar instanceof File ? avatar : '');
  }

  return { formData, hasAvatar };
}

export async function updateProfileFn(
  axiosInstance: Promise<AxiosInstance>,
  data: UpdateProfileForm,
  schema: z.ZodType<UpdateProfileForm>,
) {
  const instance = await axiosInstance;
  const { formData, hasAvatar } = createFormData(data, schema);

  if (hasAvatar) return instance.put('/profile', formData);

  return instance.put('/profile', schema.parse(data));
}

export function useUpdateProfile() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();
  const setCurrentLocale = useSetAtom(currentLanguageAtom);

  const mutation = useMutation<ApiResponse, ApiError, UpdateProfileForm>({
    mutationFn: (data: UpdateProfileForm) =>
      updateProfileFn(axiosInstance, data, UpdateProfileFormSchema),
    onSuccess: (_, context) => {
      if (isDefined(context.locale)) setCurrentLocale(context.locale);
      client.invalidateQueries({ queryKey: ['profile'] });
    },
  });

  return addActionNameToMutationResult<ApiResponse, ApiError, UpdateProfileForm, 'updateProfile'>(
    'updateProfile',
    mutation,
  );
}
